import { fetchAuthSession } from 'aws-amplify/auth';

interface ApiCallOptions {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  body?: any;
  headers?: Record<string, string>;
}

/**
 * Get the current user's access token for API calls
 */
async function getAccessToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();
  if (!token) {
    throw new Error('No access token available - user may not be signed in');
  }
  return token;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: string) {
    super(ApiError.extractMessage(status, body));
    this.name = 'ApiError';
  }

  private static extractMessage(status: number, body: string): string {
    try {
      const parsed = JSON.parse(body);
      if (typeof parsed.message === 'string') return parsed.message;
    } catch {
      // Not JSON — use raw body
    }
    return `API call failed: ${status} - ${body}`;
  }
}

export interface RawResponse<T = any> {
  status: number;
  data: T;
}

/**
 * Make authenticated API calls to the backend using JWT Bearer tokens
 */
export class ApiClient {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL;
  }

  /**
   * Make an authenticated API call
   */
  async call<T = any>({ method, path, body, headers = {} }: ApiCallOptions): Promise<T> {
    try {
      const token = await getAccessToken();

      const requestHeaders: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        ...headers,
      };

      if (body !== undefined) {
        requestHeaders['Content-Type'] = 'application/json';
      }

      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers: requestHeaders,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });

      if (response.status !== 200 && response.status !== 201) {
        const errorText = await response.text();
        throw new ApiError(response.status, errorText);
      }

      const responseText = await response.text();
      return responseText ? JSON.parse(responseText) : ({} as T);

    } catch (error) {
      console.error('API call error:', error);
      throw error;
    }
  }

  /**
   * Helper methods for common HTTP verbs
   */
  async get<T = any>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.call<T>({ method: 'GET', path, headers });
  }

  async getRaw<T = any>(path: string, headers?: Record<string, string>): Promise<RawResponse<T>> {
    const token = await getAccessToken();
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}`, ...headers },
    });
    const text = await response.text();
    const data = text ? JSON.parse(text) : ({} as T);
    return { status: response.status, data };
  }

  async post<T = any>(path: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.call<T>({ method: 'POST', path, body, headers });
  }

  async put<T = any>(path: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.call<T>({ method: 'PUT', path, body, headers });
  }

  async patch<T = any>(path: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.call<T>({ method: 'PATCH', path, body, headers });
  }

  async delete<T = any>(path: string, headers?: Record<string, string>): Promise<T> {
    return this.call<T>({ method: 'DELETE', path, headers });
  }
}

// Export a singleton instance
export const apiClient = new ApiClient();
